import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Check, 
  CreditCard, 
  Building2, 
  Phone, 
  Mail, 
  MapPin,
  Calendar,
  FileText,
  AlertCircle,
  ExternalLink,
  Banknote,
  Wallet,
  DollarSign,
  Copy,
  CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Separate component to avoid hydration warnings from tooling wrapping dynamic content
const InvoiceLineItemsTableBody = ({ items }) => (
  <tbody suppressHydrationWarning>
    {items.map((item, index) => (
      <tr key={index} className="border-t" suppressHydrationWarning>
        <td className="p-3">
          <p className="font-medium text-gray-900">{item.name}</p>
          {item.description && (
            <p className="text-sm text-gray-500">{item.description}</p>
          )}
          <p className="text-sm text-gray-500">
            {item.qty} × ${item.rate?.toFixed(2)}
          </p>
        </td>
        <td className="p-3 text-right font-medium">
          ${item.total?.toFixed(2)}
        </td>
      </tr>
    ))}
  </tbody>
);

const PublicInvoicePage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [isPaying, setIsPaying] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null); // null | 'checking' | 'paid' | 'cancelled' | 'pending'

  useEffect(() => {
    loadInvoice();
  }, [token]);

  // Handle return from Stripe Checkout: ?stripe_session_id=cs_xxx OR ?stripe_status=cancelled
  useEffect(() => {
    const url = new URL(window.location.href);
    const sessionId = url.searchParams.get('stripe_session_id');
    const stripeFlag = url.searchParams.get('stripe_status');

    if (stripeFlag === 'cancelled') {
      toast.info('Payment cancelled. You can try again anytime.');
      url.searchParams.delete('stripe_status');
      window.history.replaceState({}, '', url.toString());
      return;
    }

    if (sessionId) {
      setStripeStatus('checking');
      pollCheckoutStatus(sessionId, 0);
      // Clean the URL but keep the user on the same page
      url.searchParams.delete('stripe_session_id');
      window.history.replaceState({}, '', url.toString());
    } else {
      // No session_id in URL: silently try once to reconcile in case a recent
      // Stripe Checkout completed but the webhook hasn't reached us yet (or
      // the user closed the success tab before redirect). Safe & idempotent.
      reconcileQuietly();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reconcileQuietly = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/public/invoice/${token}/reconcile`);
      if (r.data?.reconciled) {
        toast.success('Payment confirmed — thank you!');
        setStripeStatus('paid');
        await loadInvoice();
      }
    } catch (e) {
      // Swallow — purely a best-effort fallback
    }
  };

  const pollCheckoutStatus = async (sessionId, attempt) => {
    const MAX = 8;
    try {
      const r = await axios.get(
        `${API_URL}/api/public/invoice/${token}/checkout-status?session_id=${sessionId}`
      );
      const { payment_status, session_status } = r.data;
      if (payment_status === 'paid') {
        toast.success('Payment successful — thank you!');
        setStripeStatus('paid');
        await loadInvoice();
        return;
      }
      if (session_status === 'expired' || attempt >= MAX) {
        // Last-ditch fallback: ask the backend to reconcile via the stored
        // payment_transactions row. Helpful if the customer's browser was
        // network-flaky during the redirect-back.
        try {
          const rec = await axios.post(`${API_URL}/api/public/invoice/${token}/reconcile`);
          if (rec.data?.reconciled) {
            toast.success('Payment confirmed — thank you!');
            setStripeStatus('paid');
            await loadInvoice();
            return;
          }
        } catch (_) { /* ignore */ }
        setStripeStatus('pending');
        toast.message('Payment is still processing. We will email you a confirmation once it clears.');
        return;
      }
      setTimeout(() => pollCheckoutStatus(sessionId, attempt + 1), 2000);
    } catch (e) {
      if (attempt < MAX) {
        setTimeout(() => pollCheckoutStatus(sessionId, attempt + 1), 2000);
      } else {
        setStripeStatus('pending');
      }
    }
  };

  const loadInvoice = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/invoice/${token}`);
      setData(response.data);
    } catch (err) {
      console.error('Failed to load invoice:', err);
      setError('Invoice not found or link has expired');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handlePayNow = async () => {
    // Prefer real Stripe Checkout via Connect; fall back to legacy static link.
    const stripeReady = data?.payment_capability?.stripe_connected
      && data?.payment_capability?.stripe_charges_enabled;
    if (stripeReady) {
      setIsPaying(true);
      try {
        const r = await axios.post(
          `${API_URL}/api/public/invoice/${token}/checkout`,
          { origin_url: window.location.origin }
        );
        if (r.data?.url) {
          window.location.href = r.data.url;
          return;
        }
        toast.error('Could not start payment. Please try again.');
      } catch (err) {
        const msg = err.response?.data?.detail || 'Payment failed to start';
        toast.error(msg);
      } finally {
        setIsPaying(false);
      }
      return;
    }
    // Stripe not connected → online payment unavailable (manual methods still shown below)
    toast.error('Online card payment is not available for this invoice yet.');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invoice Not Found</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const { invoice, client, organization, payment_settings } = data;
  // Fix floating point precision - round to 2 decimal places
  const balanceDue = Math.round((invoice.amount_due - (invoice.amount_paid || 0)) * 100) / 100;
  const isPaid = invoice.status === 'paid' || balanceDue <= 0.01;
  // Robust deposit detection: explicit flag, or first line item starts with "Deposit"
  const firstItemName = (invoice.line_items?.[0]?.name || '').toLowerCase();
  const isDeposit = invoice.is_deposit_invoice === true ||
    (firstItemName.startsWith('deposit') && (invoice.line_items?.length || 0) <= 2);
  
  // Primary color with fallback
  const primaryColor = organization?.brand_primary_color || '#1e3a8a';
  const accentColor = organization?.brand_accent_color || '#fbbf24';

  // Check which payment methods are enabled
  const stripeReady = data?.payment_capability?.stripe_connected
    && data?.payment_capability?.stripe_charges_enabled;
  // Card online requires both Stripe connected AND contractor's toggle ON
  const hasOnlinePayment = stripeReady && (payment_settings?.accept_card_online !== false);
  const hasVenmo = payment_settings?.accept_venmo && payment_settings?.venmo_handle;
  const hasCashApp = payment_settings?.accept_cashapp && payment_settings?.cashapp_cashtag;
  const hasCheck = payment_settings?.accept_check;
  const hasCash = payment_settings?.accept_cash;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header Bar */}
      <div 
        className="py-4 px-4"
        style={{ backgroundColor: primaryColor }}
      >
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {organization?.logo_url ? (
              <img 
                src={organization.logo_url} 
                alt={organization.company_name} 
                className="h-10 object-contain bg-white rounded px-2 py-1"
              />
            ) : (
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-xl"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
              >
                {organization?.company_name?.charAt(0) || 'C'}
              </div>
            )}
            <span className="text-white font-semibold text-lg hidden sm:block">
              {organization?.company_name}
            </span>
          </div>
          
          {isPaid && (
            <div className="bg-green-500 text-white px-4 py-2 rounded-full flex items-center gap-2 font-bold">
              <CheckCircle className="w-5 h-5" />
              PAID
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto p-4 pb-32 md:pb-8">
        {/* Invoice Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
          {/* Invoice Header */}
          <div className="p-6 border-b">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Invoice</p>
                <h1 className="text-2xl font-bold text-gray-900">
                  #{invoice.id.slice(0, 8).toUpperCase()}
                </h1>
              </div>
              
              <div className="text-right">
                <p className="text-sm text-gray-500 mb-1">
                  {isPaid ? 'Amount Paid' : isDeposit ? 'Deposit Due' : 'Balance Due'}
                </p>
                <p 
                  className="text-3xl font-bold"
                  style={{ color: isPaid ? '#16a34a' : '#dc2626' }}
                >
                  ${(isPaid ? (invoice.amount_paid || invoice.amount_due) : balanceDue).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>

          {/* Invoice Details */}
          <div className="p-6 space-y-6">
            {/* Deposit Banner */}
            {isDeposit && !isPaid && (
              <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-xl p-4 flex items-start gap-3">
                <DollarSign className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-amber-900">Deposit Required</p>
                  <p className="text-amber-800 mt-0.5">
                    This is a deposit invoice. Payment of <strong>${balanceDue.toFixed(2)}</strong> is required to schedule and begin work. The remaining balance will be invoiced upon project completion.
                  </p>
                </div>
              </div>
            )}

            {/* Quick Description (summary mode or general note) */}
            {invoice.description && invoice.display_mode !== 'summary' && (
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">About This Invoice</h3>
                <p className="text-gray-700 whitespace-pre-line">{invoice.description}</p>
              </div>
            )}

            {/* Bill To */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Bill To</h3>
              <div className="space-y-1">
                <p className="text-lg font-semibold text-gray-900">{client?.name}</p>
                {client?.email && (
                  <p className="text-gray-600 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {client.email}
                  </p>
                )}
                {client?.phone && (
                  <p className="text-gray-600 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {client.phone}
                  </p>
                )}
                {client?.address && (
                  <p className="text-gray-600 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {client.address}
                  </p>
                )}
              </div>
            </div>

            {/* Invoice Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Issue Date</p>
                <p className="font-medium">
                  {new Date(invoice.created_at).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              </div>
              {invoice.due_date && (
                <div>
                  <p className="text-sm text-gray-500">Due Date</p>
                  <p className="font-medium">
                    {new Date(invoice.due_date).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              )}
            </div>

            {/* Line Items / Summary Sections */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                {invoice.display_mode === 'summary' ? 'Details' : 'Items'}
              </h3>
              
              {/* Summary Mode Display */}
              {invoice.display_mode === 'summary' && (
                <div className="space-y-4">
                  {invoice.description && (
                    <p className="text-gray-600 mb-4">{invoice.description}</p>
                  )}
                  {invoice.summary_sections?.map((section, index) => (
                    <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="p-4" style={{ backgroundColor: `${primaryColor}10` }}>
                        <h4 className="font-semibold text-gray-900">{section.title}</h4>
                        {section.description && (
                          <p className="text-gray-600 text-sm mt-2 whitespace-pre-line">{section.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Itemized Mode Display */}
              {invoice.display_mode !== 'summary' && (
                <>
              {/* Desktop Table */}
              <div className="hidden sm:block border rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">Description</th>
                      <th className="text-right p-3 text-sm font-semibold text-gray-600">Amount</th>
                    </tr>
                  </thead>
                  <InvoiceLineItemsTableBody items={invoice.line_items || []} />
                </table>
              </div>
              
              {/* Mobile Card Layout */}
              <div className="sm:hidden space-y-3">
                {invoice.line_items?.map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 pr-4">
                        <p className="font-semibold text-gray-900">{item.name}</p>
                        {item.description && (
                          <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                        )}
                        <p className="text-sm text-gray-500 mt-1">
                          {item.qty} × ${item.rate?.toFixed(2)}
                        </p>
                      </div>
                      <p className="font-bold text-lg" style={{ color: primaryColor }}>
                        ${item.total?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
                </>
              )}
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>${invoice.amount_due?.toFixed(2)}</span>
              </div>
              {invoice.amount_paid > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Paid</span>
                  <span>-${invoice.amount_paid?.toFixed(2)}</span>
                </div>
              )}
              <div 
                className="flex justify-between text-lg font-bold pt-2 border-t"
                style={{ color: isPaid ? '#16a34a' : primaryColor }}
              >
                <span>{isPaid ? 'Paid' : isDeposit ? 'Deposit Due' : 'Balance Due'}</span>
                <span>${balanceDue.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Section - Only show if not paid */}
        {!isPaid && (
          <div className="space-y-4">
            {/* Primary Pay Button */}
            {hasOnlinePayment && (
              <Button
                onClick={handlePayNow}
                disabled={isPaying || stripeStatus === 'checking'}
                className="w-full h-16 text-xl font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all disabled:opacity-60"
                style={{ backgroundColor: primaryColor }}
                data-testid="pay-now-btn"
              >
                {isPaying || stripeStatus === 'checking' ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3" />
                    {stripeStatus === 'checking' ? 'Confirming payment…' : 'Starting secure checkout…'}
                  </>
                ) : (
                  <>
                    <CreditCard className="w-6 h-6 mr-3" />
                    {isDeposit ? 'Pay Deposit Now' : 'Pay Now'}
                    <ExternalLink className="w-5 h-5 ml-3" />
                  </>
                )}
              </Button>
            )}
            {stripeReady && hasOnlinePayment && !isPaying && stripeStatus !== 'checking' && (
              <p className="text-center text-xs text-gray-500 -mt-2" data-testid="stripe-secured-note">
                Secure checkout powered by Stripe · Your card info never touches our servers.
              </p>
            )}

            {/* Alternate Payment Methods */}
            {(hasVenmo || hasCashApp || hasCheck || hasCash) && (
              <div className="bg-white rounded-2xl shadow p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  Other Payment Options
                </h3>
                
                <div className="space-y-4">
                  {/* Venmo */}
                  {hasVenmo && (
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <span className="w-10 h-10 bg-[#008CFF] rounded-xl text-white text-sm flex items-center justify-center font-bold">V</span>
                        <div>
                          <p className="font-semibold text-gray-900">Venmo</p>
                          <p className="text-blue-600 font-medium">{payment_settings.venmo_handle}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(payment_settings.venmo_handle, 'venmo')}
                          className="h-9"
                        >
                          {copiedField === 'venmo' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                        {payment_settings.venmo_link && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(payment_settings.venmo_link, '_blank')}
                            className="h-9"
                          >
                            Open <ExternalLink className="w-3 h-3 ml-1" />
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Cash App */}
                  {hasCashApp && (
                    <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <span className="w-10 h-10 bg-[#00D632] rounded-xl text-white text-sm flex items-center justify-center font-bold">$</span>
                        <div>
                          <p className="font-semibold text-gray-900">Cash App</p>
                          <p className="text-green-600 font-medium">{payment_settings.cashapp_cashtag}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(payment_settings.cashapp_cashtag, 'cashapp')}
                          className="h-9"
                        >
                          {copiedField === 'cashapp' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                        {payment_settings.cashapp_link && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(payment_settings.cashapp_link, '_blank')}
                            className="h-9"
                          >
                            Open <ExternalLink className="w-3 h-3 ml-1" />
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Check */}
                  {hasCheck && (
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3 mb-3">
                        <FileText className="w-5 h-5 text-gray-600" />
                        <p className="font-semibold text-gray-900">Pay by Check</p>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1 ml-8">
                        <p>
                          <span className="font-medium">Make payable to:</span>{' '}
                          {payment_settings.check_payable_to || organization?.company_name}
                        </p>
                        {(payment_settings.check_mailing_address || organization?.address) && (
                          <p>
                            <span className="font-medium">Mail to:</span>{' '}
                            {payment_settings.check_mailing_address || organization?.address}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Cash */}
                  {hasCash && (
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <Banknote className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="font-semibold text-gray-900">Cash</p>
                          <p className="text-sm text-gray-600">Accepted in person</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Payment Instructions */}
                {payment_settings?.payment_instructions && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-500 font-medium mb-2">Additional Instructions</p>
                    <p className="text-gray-600 text-sm whitespace-pre-wrap">
                      {payment_settings.payment_instructions}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Company Footer */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p className="font-medium text-gray-700">{organization?.company_name}</p>
          {organization?.phone && <p>{organization.phone}</p>}
          {organization?.email && <p>{organization.email}</p>}
        </div>
      </div>

      {/* Mobile Sticky Pay Button */}
      {!isPaid && hasOnlinePayment && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg md:hidden">
          <Button
            onClick={handlePayNow}
            disabled={isPaying || stripeStatus === 'checking'}
            className="w-full h-14 text-lg font-bold rounded-xl disabled:opacity-60"
            style={{ backgroundColor: primaryColor }}
            data-testid="pay-now-mobile-btn"
          >
            {isPaying || stripeStatus === 'checking' ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                {stripeStatus === 'checking' ? 'Confirming…' : 'Starting…'}
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5 mr-2" />
                {isDeposit ? 'Pay Deposit' : 'Pay Now'} - ${balanceDue.toFixed(2)}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PublicInvoicePage;
